import { useState, useEffect, useCallback, useMemo } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { UnitApi } from '@/api';
import { IUnitStatusOverview } from '@/api/types';
import { SearchBar } from '@/components';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';

import EditUnitStatusModal from './EditUnitStatusModal';
import TimerDisplay from './TimerDisplay';

const UnitStatusTab = () => {
  const [unitStatuses, setUnitStatuses] = useState<IUnitStatusOverview[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUnit, setEditingUnit] = useState<IUnitStatusOverview | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { updateToast } = useToastStore();
  const { account } = useUserStore();
  const [rowsExceedingThreshold, setRowsExceedingThreshold] = useState<
    Record<string, boolean>
  >({});
  const [searchText, setSearchText] = useState<string>('');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await UnitApi.getUnitStatusOverview();
      const responseData = response?.data;
      const editableUnits = Array.isArray(responseData)
        ? responseData.filter((unit) => !!unit.latestShiftId)
        : [];
      setUnitStatuses(editableUnits);
    } catch (err: any) {
      setError('Failed to load unit status data. Please try again later.');
      updateToast({
        open: true,
        message: 'Could not fetch unit statuses.',
        type: ConfirmTypes.WARNING,
      });
      setUnitStatuses([]);
    } finally {
      setIsLoading(false);
    }
  }, [updateToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenEditModal = (unit: IUnitStatusOverview) => {
    setEditingUnit(unit);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUnit(null);
  };

  const handleSaveSuccess = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const handleThresholdChange = useCallback(
    (unitId: string, exceeded: boolean) => {
      setRowsExceedingThreshold((prev) => ({
        ...prev,
        [unitId]: exceeded,
      }));
    },
    [],
  );

  const handleSearchChange = (value: string) => {
    setSearchText(value);
  };

  const filteredUnitStatuses = useMemo(() => {
    if (!searchText) {
      return unitStatuses;
    }
    const lowerCaseSearchText = searchText.toLowerCase();
    return unitStatuses.filter(
      (unit) =>
        unit.name.toLowerCase().includes(lowerCaseSearchText) ||
        unit.agency?.name?.toLowerCase().includes(lowerCaseSearchText),
    );
  }, [unitStatuses, searchText]);

  const renderSkeleton = () =>
    Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        <TableCell>
          <Skeleton variant="text" width={100} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={150} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={80} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={120} />
        </TableCell>
        <TableCell align="right" sx={{ pr: 0 }}>
          <Skeleton variant="rectangular" width={30} height={30} />
        </TableCell>
      </TableRow>
    ));

  if (error) {
    return (
      <Box
        p={2}
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      width="100%"
      marginX={1}
      height="100%"
      display="flex"
      flexDirection="column"
    >
      {/* Search Bar */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          width: '100%',
          padding: 1,
        }}
      >
        <SearchBar
          placeholder="Search Unit/Agency"
          onChange={handleSearchChange}
          value={searchText}
          sx={{ width: '100%' }}
        />
      </Box>

      {/* Table Container */}
      <TableContainer
        sx={{
          flexGrow: 1,
          maxHeight: 'calc(100vh - 300px)',
          overflow: 'auto',
        }}
      >
        <Table size="small" stickyHeader aria-label="Unit Status Table">
          <TableHead
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              bgcolor: 'background.paper',
            }}
          >
            <TableRow>
              <TableCell sx={{ pl: 1 }}>Unit</TableCell>
              <TableCell>Agency</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Status</TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: 'bold', py: 1, pr: 1 }}
              ></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              renderSkeleton()
            ) : unitStatuses.length > 0 ? (
              filteredUnitStatuses.length > 0 ? (
                filteredUnitStatuses.map((unit) => (
                  <TableRow
                    key={unit._id}
                    hover
                    sx={(theme) => ({
                      bgcolor: rowsExceedingThreshold[unit._id]
                        ? 'error.light'
                        : 'inherit',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    })}
                  >
                    <TableCell sx={{ fontWeight: 'medium' }}>
                      {unit.name}
                    </TableCell>
                    <TableCell>{unit.agency?.name ?? 'N/A'}</TableCell>
                    <TableCell>
                      <TimerDisplay
                        customStatusUpdatedAt={unit.customStatusUpdatedAt}
                        isAssignedToCFS={unit.isAssignedToCFS}
                        onThresholdExceededChange={(exceeded) =>
                          handleThresholdChange(unit._id, exceeded)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {account?.settings?.features?.isEnabledCustomUnitStatus
                        ? unit.customStatus?.description ?? 'None'
                        : 'None'}
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 0 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditModal(unit)}
                        aria-label={`Edit status for unit ${unit.name}`}
                        disabled={!unit.latestShiftId}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">
                      No units match your search.
                    </Typography>
                  </TableCell>
                </TableRow>
              )
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">
                    No unit statuses found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <EditUnitStatusModal
        unit={editingUnit}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSaveSuccess={handleSaveSuccess}
      />
    </Box>
  );
};

export default UnitStatusTab;
